import React, { useEffect, useState, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowDownToArc,
  faArrowsLeftRight,
  faArrowsUpDown,
  faTruckFast,
  faWeightHanging,
} from '@fortawesome/pro-solid-svg-icons'
import { Button } from '../../../components/shadcn/Button'
import { printTicket, wait } from '../../../lib'
import { useForm, FormProvider } from 'react-hook-form'
import { useOrderDetailContext } from '../OrderDetailContext'
import { FromPackage } from './OrderItemsInPackages'
import { ControlledCompactInput } from '../../../components/Form/Controlled/ControlledCompactInput'
import { ControlledCompactSelect } from '../../../components/Form/Controlled/ControlledCompactSelect'
import { useQueryClient } from '@tanstack/react-query'
import { graphql, useFragment } from '../../../gql'
import { useGQLMutation } from '../../../hooks/GQLUtilityHooks'
import { toastSuccess, toastError, toastPromise } from '../../../lib/toastHelpers'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/shadcn/tooltip'
import { useCore } from '../../../hooks'
import { notifyPickingActivity } from '../lib/hooks/pickingActivityEvents'

const sendPackageMutation = graphql(`
  mutation SendPackage($id: Int!) {
    sendPackage(id: $id) {
      errors
    }
  }
`)

const updateOrderItemMutation = graphql(`
  mutation UpdateOrderItemPacking($id: Int!, $packed: Boolean!) {
    updateOrderItem(id: $id, packed: $packed) {
      data {
        id
        packed
      }
      errors
    }
  }
`)

export const PackageDetail_OrderFragment = graphql(`
  fragment PackageDetail_OrderFragment on Order {
    id
    carrier {
      id
      name
      requiredShippingAddress
      tariffs {
        id
        name
      }
    }
    shippingAddress {
      id
    }
    packages {
      ticketUrl
      id
      code
      sent
      carrier {
        id
        name
        tariffs {
          id
          name
        }
      }
    }
  }
`)

type Props = {
  pack: FromPackage
  index: number
  isMoving?: boolean
  onMovePressed?: () => void
  savePackages: () => void
  onPackageChange?: (packageId: number, data: Omit<FromPackage, 'packageId' | 'items' | 'code' | 'carrier'>) => void
  orderItems?: any[]
  processStatusId?: string
}

type PackageFormData = Omit<FromPackage, 'packageId' | 'items' | 'code' | 'carrier'>

export const PackageDetail: React.FC<Props> = ({
  pack,
  index,
  isMoving,
  onMovePressed,
  savePackages,
  onPackageChange,
  orderItems,
  processStatusId,
}) => {
  const [isListeningForScale, setIsListeningForScale] = useState<boolean>(false)

  const context = useOrderDetailContext()
  const queryClient = useQueryClient()
  const order = useFragment(PackageDetail_OrderFragment, context.order)
  const { core } = useCore()

  const serverPack = order?.packages?.find(p => p.id === pack.packageId)

  const form = useForm<PackageFormData>({
    defaultValues: {
      tariff: pack.tariff,
      weight: pack.weight,
      dimensionX: pack.dimensionX,
      dimensionY: pack.dimensionY,
      dimensionZ: pack.dimensionZ,
    },
  })

  const { control, handleSubmit, formState, watch, reset, setValue } = form

  const readWeight = () => {
    if (isListeningForScale) {
      setIsListeningForScale(false)
    } else {
      setIsListeningForScale(true)

      if (window.weightHistory?.length) {
        setValue('weight', parseFloat(window.weightHistory[window.weightHistory.length - 1]))
        setIsListeningForScale(false)
        if (window.weightHistory.length > 100) {
          window.weightHistory = []
        }
      }
    }
  }

  // Update form values when pack changes
  useEffect(() => {
    reset({
      tariff: pack.tariff,
      weight: pack.weight,
      dimensionX: pack.dimensionX,
      dimensionY: pack.dimensionY,
      dimensionZ: pack.dimensionZ,
    })
  }, [pack.tariff, pack.weight, pack.dimensionX, pack.dimensionY, pack.dimensionZ])

  // Watch for changes and notify parent
  useEffect(() => {
    const subscription = watch(value => {
      if (onPackageChange) {
        onPackageChange(pack.packageId, value as PackageFormData)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, pack.packageId, onPackageChange])

  // Auto-save with debounce (2 seconds after last edit)
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    const subscription = watch(() => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
      debounceTimer.current = setTimeout(() => {
        savePackages()
      }, 2000)
    })
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
      subscription.unsubscribe()
    }
  }, [watch])

  const tariffs = (serverPack?.carrier?.tariffs || []).map(t => ({
    value: String(t.id),
    label: t.name,
  }))

  const handleSendPackage = async () => {
    if (order?.carrier?.requiredShippingAddress && !order?.shippingAddress) {
      alert('Není nastavena doručovací adresa')
      return
    }
    if (formState.isDirty) {
      alert('Mate neuložené změny u balíku')
      return
    }
    sendPackage({ id: pack.packageId })
  }

  const { mutate: sendPackage, isPending: isSendingPackage } = useGQLMutation(
    sendPackageMutation,
    ['sendPackage', pack.packageId],
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['order', { id: order.id }] })
        toastSuccess('Balík odeslán!')
      },
      errorPrefix: 'Chyba dopravce',
    },
  )

  const printPackageTicket = async () => {
    if (serverPack?.ticketUrl && serverPack.carrier?.id) {
      toastPromise(
        new Promise(async (resolve, reject) => {
          try {
            await printTicket(
              {
                ticketUrl: serverPack.ticketUrl,
                carrier: { id: serverPack.carrier.id },
              },
              core,
            )
            await wait()
            resolve(null)
          } catch (error) {
            reject(error)
          }
        }),
        {
          success: 'Tisk stitku byl pridan do fronty',
          error: 'Chyba při vytisknutí štítku pro balík',
          pending: 'Vytisknutí štítku pro balík...',
        },
      )
    } else {
      toastError('Není k dispozici štítek pro tento balík')
    }
  }

  const { mutateAsync: packOrderItem } = useGQLMutation(updateOrderItemMutation, ['updateOrderItemPacking'])
  const [isPacking, setIsPacking] = useState(false)

  // Handler for marking all as packed
  const handlePackAll = async () => {
    if (!orderItems) return
    setIsPacking(true)
    try {
      await Promise.all(orderItems.filter(oi => !oi.packed).map(oi => packOrderItem({ id: oi.id, packed: true })))
      queryClient.invalidateQueries({ queryKey: ['order'] })
      if (order?.id) {
        notifyPickingActivity({ orderId: Number(order.id), phase: 'packing', source: 'package-detail' })
      }
    } catch (e) {
      toastError('Chyba při označení položek jako zabalené')
    } finally {
      setIsPacking(false)
    }
  }

  return (
    <FormProvider {...form}>
      <div className={'flex gap-4 justify-between flex-1 py-2 px-4'}>
        <div className={'flex items-center gap-4'}>
          {serverPack?.carrier?.tariffs?.length > 0 && (
            <div className={'flex items-center gap-2'}>
              <FontAwesomeIcon icon={faTruckFast} className={'text-shadow-grey'} size={'lg'} />
              <ControlledCompactSelect
                control={control}
                name={'tariff'}
                options={tariffs}
                label={'Tarif'}
                className={'w-64'}
                onChange={() => {
                  savePackages()
                }}
              />
            </div>
          )}

          <div className={'flex items-center'}>
            <div className={'flex items-center gap-2'}>
              <FontAwesomeIcon icon={faWeightHanging} className={'text-shadow-grey'} size={'lg'} />
              <ControlledCompactInput
                label={'Hmotnost'}
                right={'kg'}
                control={control}
                name={'weight'}
                className={`w-32 ${'_depoto' in window ? 'rounded-r-none' : ''}`}
                inputClassName={'text-right'}
                type={'number'}
                rules={{ required: true, pattern: /^\d+(\.\d+)?$/ }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    savePackages()
                  }
                }}
              />
            </div>
            {'_depoto' in window && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    onClick={readWeight}
                    variant={isListeningForScale ? 'destructive' : 'default'}
                    className={`rounded-l-none h-10 px-6`}>
                    {true ? (
                      <FontAwesomeIcon
                        icon={['far', 'weight-scale']}
                        size="lg"
                        color={isListeningForScale ? '#000' : '#fff'}
                      />
                    ) : null}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isListeningForScale ? 'Probíhá načítání hmotnosti' : 'Načíst hmotnost z váhy'}
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          <div className={'flex items-center gap-2'}>
            <FontAwesomeIcon icon={faArrowsLeftRight} className={'text-shadow-grey'} size={'lg'} />
            <ControlledCompactInput
              label={'Šířka'}
              right={'cm'}
              control={control}
              name={'dimensionX'}
              className={'w-32'}
              inputClassName={'text-right'}
              type={'number'}
              rules={{ required: true, pattern: /^\d+(\.\d+)?$/ }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  savePackages()
                }
              }}
            />
          </div>
          <div className={'flex items-center gap-2'}>
            <FontAwesomeIcon icon={faArrowsUpDown} className={'text-shadow-grey'} size={'lg'} />
            <ControlledCompactInput
              label={'Výška'}
              right={'cm'}
              control={control}
              name={'dimensionY'}
              inputClassName={'text-right'}
              className={'w-32'}
              type={'number'}
              rules={{ required: true, pattern: /^\d+(\.\d+)?$/ }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  savePackages()
                }
              }}
            />
          </div>
          <div className={'flex items-center gap-2'}>
            <FontAwesomeIcon icon={faArrowDownToArc} className={'text-shadow-grey'} size={'lg'} />
            <ControlledCompactInput
              label={'Hloubka'}
              right={'cm'}
              control={control}
              name={'dimensionZ'}
              inputClassName={'text-right'}
              className={'w-32'}
              type={'number'}
              rules={{ required: true, pattern: /^\d+(\.\d+)?$/ }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  savePackages()
                }
              }}
            />
          </div>
        </div>
        <div className={'flex items-center gap-2 min-h-14'}>
          {isMoving && (
            <Button
              variant="default"
              className={'bg-orange text-white hover:bg-orange-light'}
              icon={['far', 'arrows-up-down-left-right']}
              title={'Přesunout sem'}
              onClick={onMovePressed}>
              {'Přesunout sem'}
            </Button>
          )}
          {orderItems && processStatusId === 'packing' && orderItems.some(oi => !oi.packed) && (
            <Button
              variant="outline"
              icon={['far', 'check-square']}
              title={'Označit vše jako zabalené'}
              disabled={isPacking}
              onClick={handlePackAll}>
              {isPacking ? 'Označuji...' : 'Označit vše'}
            </Button>
          )}
          {serverPack && !serverPack.sent && (
            <Button variant="default" title={'Poslat balik'} onClick={handleSendPackage} disabled={isSendingPackage}>
              {isSendingPackage ? 'Posílám...' : 'Poslat balík'}
            </Button>
          )}
          {serverPack && serverPack.sent && (
            <Button variant="default" icon={['far', 'print']} title={'Vytisknout štítek'} onClick={printPackageTicket}>
              {'Vytisknout štítek'}
            </Button>
          )}
        </div>
      </div>
    </FormProvider>
  )
}
